<template>
    <div class="offcanvas offcanvas-lg offcanvas-end show" style="visibility: visible;" v-if="is_open">
        <div class="offcanvas-header">
            <h3 class="offcanvas-title">{{ user?.user_name }} さん - {{ dateMdw(date) }}</h3>
            <div class="ms-5 me-auto">
                <router-link
                    class="btn btn-outline-primary"
                    :to="{name: 'ReportEdit', params: {report: report.report_id}}"
                    v-if="canEdit"
                >
                    <i class="bi bi-pencil"></i> 編集
                </router-link>
            </div>
            <!-- 切替ボタンはタイトル（ユーザー名）の長さで位置が動かないよう、閉じるボタンの左に固定する -->
            <div class="btn-group me-4" role="group" aria-label="日報の切替">
                <button type="button" class="btn btn-outline-secondary" aria-label="左の人の日報" title="左の人の日報" :disabled="!has_prev" @click="$emit('prev')"><i class="bi bi-chevron-left" aria-hidden="true"></i></button>
                <button type="button" class="btn btn-outline-secondary" aria-label="右の人の日報" title="右の人の日報" :disabled="!has_next" @click="$emit('next')"><i class="bi bi-chevron-right" aria-hidden="true"></i></button>
                <button type="button" class="btn btn-outline-secondary" aria-label="再読み込み" title="再読み込み" :disabled="is_loading" @click="$emit('reload')"><i class="bi bi-arrow-clockwise" aria-hidden="true"></i></button>
            </div>
            <button type="button" class="btn-close text-reset" @click="$emit('close')"></button>
        </div>
        <div class="offcanvas-body">
            <p class="text-muted" v-if="!is_loading && !view_report">
                <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                日報は登録されていません。<span v-if="holiday">（休日として登録されています）</span>
            </p>
            <div class="table-responsive" v-else>
            <table class="table table-bordered">
                <thead>
                    <tr>
                        <th class="nowrap">案件</th>
                        <th class="nowrap">時間</th>
                        <th class="nowrap">(請求)</th>
                        <th class="nowrap">フェーズ/カテゴリ</th>
                        <th class="nowrap">作業内容</th>
                    </tr>
                </thead>
                <tbody v-if="is_loading">
                    <tr v-for="i in Array(skeleton_rows)" :key="i">
                        <td><text-loader :width="14"></text-loader></td>
                        <td class="text-center"><text-loader :width="4"></text-loader></td>
                        <td class="text-center"><text-loader :width="4"></text-loader></td>
                        <td><text-loader :width="10"></text-loader></td>
                        <td><text-loader :width="14"></text-loader></td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="work of view_report.works" :key="work.work_id">
                        <td>{{ work.project?.project_name }}</td>
                        <td class="text-center nowrap">{{ $helper.clock(work.work_time) }}</td>
                        <td class="text-center nowrap">{{ $helper.clock(work.charge_time) }}</td>
                        <td>{{ work.phase?.phase_name }}{{ work.work_category?.category_name }}</td>
                        <td>{{ work.work_note }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th>合計</th>
                        <td class="text-center">{{ is_loading ? '' : $helper.clock(view_report.work_time_total) }}</td>
                        <td colspan="3"></td>
                    </tr>
                </tfoot>
            </table>
            </div>
        </div>
    </div>
    <div class="offcanvas-backdrop fade show" v-if="is_open" @click="$emit('close')"></div>
</template>

<script>
import { dateMdw } from '@/utilities/date-format'
import { isBlank } from '@/utilities/typing'
import Report from "@/models/entities/report";
import TextLoader from '@/components/tools/TextLoader';

export default {
    name: 'PartViewReport',
    components: {
        TextLoader,
    },
    props: {
        // 表示対象の日報 (未登録の場合は null)
        report: {
            type: Object,
            default: null,
        },
        // 表示対象のユーザー (日報未登録でもヘッダに表示するため別 prop で受ける)
        user: {
            type: Object,
            default: null,
        },
        // 表示対象日 (Y-m-d)
        date: {
            type: String,
            default: null,
        },
        // 休日として登録されているか
        holiday: {
            type: Boolean,
            default: false,
        },
        // 親側で日報一覧を読み込み中か
        loading: {
            type: Boolean,
            default: false,
        },
        has_prev: {
            type: Boolean,
            default: false,
        },
        has_next: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            detail_loading: false,
            view_report: null,
            dateMdw,
        }
    },
    emits: [
        'close',
        'prev',
        'next',
        'reload',
    ],
    computed: {
        // ユーザーと日付が決まっていればパネルを開く (日報未登録でも開く)
        is_open() {
            return !!(this.user && this.date);
        },
        is_loading() {
            return this.loading || this.detail_loading;
        },
        skeleton_rows() {
            return this.view_report?.works?.length || 3;
        },
        canEdit() {
            if (!this.report || !this.report.report_id) {
                return false;
            }
            return this.report.user?.user_id === this.$store.state.auth.user_id
                || this.$store.getters['auth/canEditOthersReport']();
        },
    },
    watch: {
        report: {
            immediate: true,
            handler() {
                this.view_report = this.report;
                this.detail_loading = false;

                // 詳細未取得であれば取得する (works[0].project.project_name の有無で判断)
                if (this.report && this.report.report_id && this.report.works.length > 0 && isBlank(this.report.works[0].project.project_name) ) {
                    this.detail_loading = true;
                    const requested_report_id = this.report.report_id;
                    this.$http.get(`/report/${requested_report_id}`)
                    .then((response) => {
                        // 応答待ちの間に別の日報へ切り替わっていた場合は破棄する
                        if (this.report?.report_id !== requested_report_id) {
                            return;
                        }
                        this.view_report = new Report(response.data);
                        this.detail_loading = false;
                    })
                    .catch(() => {
                        this.detail_loading = false;
                    })
                }
            },
        },
    },
}
</script>

<style scoped>

</style>
